import queryString from 'query-string';

import {
    FEATURE_FLAG_HOTJAR_FEEDBACK,
    FEATURE_FLAG_HOTJAR_TRACKING,
} from 'phoenix2/state/feature-flags';
import { isInTrainingMode } from './training';

const isFlagEnabled = (settings, flagName) =>
    Boolean(settings && settings.flags && settings.flags[flagName]);

export const isCookieEnabled = (window, cookieName) =>
    Boolean(window && window.cookiePreferences && window.cookiePreferences[cookieName]);

const triggerHotjar = (flagName, cookieName) => (settings, window, eventName) => {
    const flagEnabled = isFlagEnabled(settings, flagName);
    const cookieEnabled = isCookieEnabled(window, cookieName);
    if (cookieEnabled && flagEnabled && window.hj) {
        // We add the student's ID as a query string to the current URL so that the ID is available in Hotjar surveys.
        // See NOTE in identifyUserToHotJar below.
        if (window.studentID) {
            const qs = queryString.parse(location.search);
            qs['student_id'] = window.studentID;
            history.pushState(
                null,
                '',
                // NOTE: We have to use an absolute URL here as we set a <base href="..."> in test and production which
                // points at the domain that the bundle is served from.
                // - origin, e.g. https://cabbage.test2.test.sparxmaths.uk
                // - pathname, e.g. /student
                `${location.origin}${location.pathname}?${queryString.stringify(qs)}`,
            );
        }
        // NOTE: We run the trigger after a short timeout to give the path change above time to apply.
        setTimeout(() => {
            window.hj('trigger', eventName);
        }, 200);
    }
};

const triggerHotjarEventTracking = cookieName => action => {
    const trainingEnabled = isInTrainingMode();
    const cookieEnabled = isCookieEnabled(window, cookieName);
    if (trainingEnabled && cookieEnabled && window.hj) {
        window.hj('event', action);
    }
};

// If the flag is on and the cookie enabled, trigger an event for the eventName. Whether the
// eventName is a tracking event or a feedback event depends on how the event was created in
// Hotjar.
export const triggerTrackingHotjarIfEnabled = triggerHotjar(FEATURE_FLAG_HOTJAR_TRACKING, 'PT');
export const triggerFeedbackHotjarIfEnabled = triggerHotjar(FEATURE_FLAG_HOTJAR_FEEDBACK, 'Hotjar');
export const triggerEventTrackingHotjarIfEnabled = triggerHotjarEventTracking('Hotjar');

/**
 Send given userID and attributes to hotjar
 Both arguments are optional, but a userID must be provided if the attributes contain PII
 * @param  {string} userID - The users UUID
 * @param  attributes - Object containing key values for use attributes to send to hotjar. Keys are kept in hotjar so consider not setting spurious ones if testing.
 */
const sendHotjarUserAttributes = (userID, attributes) => {
    // queue events if hotjar isn't loaded for whatever reason
    window.hj =
        window.hj ||
        function() {
            (hj.q = hj.q || []).push(arguments); // eslint-disable-line no-undef
        };
    window.hj('identify', userID, attributes);
};

/**
 * Identify the current user to hotjar
 * @param  {string} userID - The users UUID
 */
export const identifyUserToHotJar = userID => {
    sendHotjarUserAttributes(userID, { user_id: userID });
    // NOTE: A bug in Hotjar means that the user ID sent above doesn't make it through.
    // Until this is fixed, we have to hackily put the student ID in the URL so that we can keep track of which student
    // submitted what feedback.
    window.studentID = userID;
};
